/**
 * PriorityNotificationPopup — Floating toast for priority notifications.
 *
 * Most notifications are silent and only surface in the NotificationCenter
 * dropdown (bell). Notifications flagged `priority: true` (errors by default)
 * also render here as a floating toast so the user sees them immediately.
 *
 * Non-priority notifications are NOT rendered here — by design — so that
 * normal info/success/warning messages stay quietly in the bell tray.
 *
 * See: docs/features/infrastructure/notification-system.md
 */
import React from "react";
import { CircleCheck, CircleX, Info, X } from "lucide-react";
import { useUnifiedNotifications, NOTIF_TYPE } from "../../contexts/UnifiedNotificationContext";
import "../../css/Notifications.css";

const iconFor = (type) => {
  switch (type) {
    case NOTIF_TYPE.SUCCESS:
      return <CircleCheck className="notification-icon-success" />;
    case NOTIF_TYPE.ERROR:
      return <CircleX className="notification-icon-error" />;
    case NOTIF_TYPE.WARNING:
      return <Info className="notification-icon-warning" />;
    default:
      return <Info className="notification-icon-info" />;
  }
};

export default function PriorityNotificationPopup() {
  const { localNotifications, dismissNotification } = useUnifiedNotifications();
  const priorityNotifs = localNotifications.filter((n) => n.priority);

  if (priorityNotifs.length === 0) return null;

  return (
    <div className="notification-container">
      {priorityNotifs.map((notif) => (
        <div
          key={notif.id}
          className={`notification notification-${notif.type}${notif.onClick ? " notification-clickable" : ""}`}
          onClick={() => {
            if (notif.onClick) notif.onClick();
            dismissNotification(notif.id);
          }}
          role="alert"
        >
          <div className="notification-icon">{iconFor(notif.type)}</div>
          <div className="notification-message">{notif.message}</div>
          <button
            className="notification-dismiss"
            onClick={(e) => {
              e.stopPropagation();
              dismissNotification(notif.id);
            }}
            aria-label="Dismiss"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              marginLeft: 12,
              display: "flex",
              alignItems: "center",
              color: "inherit",
              opacity: 0.7,
            }}
          >
            <X size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}
